/** ---------------------------------------------------------------------------------------
 * --  LICENSE AGREEMENT -- *
 This software is licensed under the following terms:

 * -- GRANT OF LICENSE | RESTRICTIONS -- *
 Subject to the terms and conditions of this license, the copyright holders
 hereby grant you a non-exclusive, non-transferable, revocable license to use,
 copy, modify, and distribute the software.
 You are expressly prohibited from selling or commercially exploiting the software.
 Unauthorized use, reproduction, or distribution of the software, or any part
 thereof, is strictly prohibited.

 * -- AUTHORIZED USERS | INTELLECTUAL PROPERTY -- *
 This license is granted only to the individual or entity to whom the software is
 directly distributed by the copyright holders. Any use by other individuals or
 entities is unauthorized and in violation of this license. The software is
 protected by copyright and other intellectual property laws.
 The copyright holders retain all rights, title, and interest in the software.

 * -- GOVERNING LAW -- *
 This license is governed by and construed in accordance with the laws of Romania.
 Any legal action or proceeding related to this license shall be brought exclusively
 in the courts of Romania.

 [Weddify x Diaphan Stationery] © [2024] All rights reserved.
 -------------------------------------------------------------------------------------- **/

import React, {useState, useEffect} from "react";
import Image from "next/image";

interface AppImageScrollProps {
    imageSource?: any;
    enableEffects?: boolean;
    height?: string | number;
    disableRandom?: boolean;
}

function AppImageScroll({imageSource, enableEffects = true, height = "105vh"}: AppImageScrollProps) {

    const src = imageSource;

    const [offsetY, setOffsetY] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const [scale, setScale] = useState(1.05);

    useEffect(() => {
        if (!enableEffects) return;

        let animationFrameId: number;
        let startTime: number | null = null;

        const animate = (time: number) => {
            if (!startTime) startTime = time;
            const elapsed = time - startTime;
            const newScale = 1.05 + Math.sin(elapsed * 0.00105) * 0.02;
            setScale(newScale);
            animationFrameId = requestAnimationFrame(animate);
        };

        animationFrameId = requestAnimationFrame(animate);

        return () => cancelAnimationFrame(animationFrameId);
    }, [enableEffects]);

    useEffect(() => {
        if (!enableEffects) {
            setOffsetY(0);
            setScale(1);
            return;
        }

        const handleScroll = () => {
            setOffsetY(window.scrollY);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, [enableEffects]);

    const translateY = enableEffects ? offsetY * 0.5 : 0;
    const appliedScale = enableEffects ? scale : 1;

    return (
        <div
            className="relative w-full overflow-hidden"
            style={{height}}>
            <Image
                key={src}
                src={imageSource}
                onLoad={() => setLoaded(true)}
                fill={true}
                alt="Weddify By Diaphan"
                className={`absolute top-0 left-0 w-full opacity-60 object-cover transition-all duration-[2000ms] ease-out ${
                    loaded ? "blur-0" : ""
                }`}
                style={{
                    height: "100%",
                    transform: `translateY(${translateY}px) scale(${appliedScale})`,
                }}
            />
        </div>
    );
}

export default AppImageScroll;
